import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ContentFrame, { PageHeading, SectionHeading, contentTextSx } from '../components/assessment/ContentFrame'
import QuestionCard, { isAnswered, neededCount } from '../components/assessment/QuestionCard'
import QuestionNavigator from '../components/assessment/QuestionNavigator'
import { errorMessage, getEnrolment, isOk, startAttempt, submitAttempt } from '../helpers/trainingApi'

// Answers survive a refresh: kept per attempt in sessionStorage until the
// attempt is submitted. Storage can be unavailable (private windows), in
// which case the answers simply live in component state.
const storageKey = (attemptId) => `training-attempt-${attemptId}`
const readSaved = (attemptId) => {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(storageKey(attemptId)) ?? 'null')
    return saved && typeof saved === 'object' ? saved : null
  } catch (e) {
    return null
  }
}
const writeSaved = (attemptId, value) => {
  try {
    window.sessionStorage.setItem(storageKey(attemptId), JSON.stringify(value))
  } catch (e) {
    // nothing to do: state still holds the answers
  }
}
const clearSaved = (attemptId) => {
  try {
    window.sessionStorage.removeItem(storageKey(attemptId))
  } catch (e) {
    // ignore
  }
}

// Drops anything saved that no longer matches the questions in the attempt.
const sanitiseAnswers = (saved, questions) => {
  const clean = {}
  questions.forEach((question) => {
    const optionIds = new Set(question.options.map((option) => option.id))
    const chosen = Array.isArray(saved?.[question.id]) ? saved[question.id].filter((id) => optionIds.has(id)) : []
    if (chosen.length) clean[question.id] = chosen.slice(0, neededCount(question))
  })
  return clean
}

const Assessment = () => {
  const { enrolmentId } = useParams()
  const coursePath = `/training/course/${enrolmentId}`

  // 'loading' | 'loadError' | 'closed' | 'start' | 'questions' | 'result'
  const [step, setStep] = useState('loading')
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')
  const [busy, setBusy] = useState(false)
  const [attempt, setAttempt] = useState(null)
  const [answers, setAnswers] = useState({})
  const [index, setIndex] = useState(0)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [result, setResult] = useState(null)
  const headingRef = useRef(null)

  const load = useCallback(async () => {
    const res = await getEnrolment(enrolmentId)
    if (!isOk(res)) {
      setLoadError(errorMessage(res, 'We could not load this course. Please try again.'))
      setStep('loadError')
      return
    }
    setData(res.data)
    setStep(res.data.assessment?.open ? 'start' : 'closed')
  }, [enrolmentId])

  useEffect(() => {
    setStep('loading')
    load()
  }, [load])

  // Move focus to the heading whenever the screen changes.
  useEffect(() => {
    if (step !== 'loading') headingRef.current?.focus()
  }, [step, index])

  useEffect(() => {
    if (attempt && step === 'questions') writeSaved(attempt.attemptId, { answers, index })
  }, [attempt, answers, index, step])

  const course = data?.content?.course
  const title = data?.tenantCourseTitle || course?.title || 'Course'
  const passMark = attempt?.passMark ?? course?.passMark
  const questions = attempt?.questions ?? []
  const total = questions.length
  const onReview = index >= total

  const answeredIds = useMemo(
    () => new Set(questions.filter((question) => isAnswered(question, answers)).map((question) => question.id)),
    [questions, answers],
  )
  const unanswered = questions
    .map((question, questionIndex) => ({ question, questionIndex }))
    .filter(({ question }) => !answeredIds.has(question.id))

  const begin = async () => {
    setBusy(true)
    setActionError('')
    const res = await startAttempt(enrolmentId)
    setBusy(false)
    if (!isOk(res)) {
      // The gate changed since the page loaded: show the reason.
      if (res.status === 409) {
        await load()
        return
      }
      setActionError(errorMessage(res, 'We could not start the assessment. Please try again.'))
      return
    }
    const saved = readSaved(res.data.attemptId)
    setAttempt(res.data)
    setAnswers(sanitiseAnswers(saved?.answers, res.data.questions ?? []))
    setIndex(Math.min(Math.max(Number(saved?.index) || 0, 0), res.data.questions?.length ?? 0))
    setStep('questions')
  }

  const submit = async () => {
    setConfirmOpen(false)
    setBusy(true)
    setActionError('')
    const payload = Object.fromEntries(questions.map((question) => [question.id, answers[question.id] ?? []]))
    const res = await submitAttempt(attempt.attemptId, payload)
    setBusy(false)
    if (!isOk(res)) {
      setActionError(errorMessage(res, 'We could not submit your answers. Please try again.'))
      return
    }
    clearSaved(attempt.attemptId)
    setResult(res.data)
    setStep('result')
  }

  const bankLink = data?.locksOff ? (
    <Typography sx={{ mt: 3 }}>
      Reviewer: <RouterLink to={`${coursePath}/bank`}>View the full question bank with answers</RouterLink>
    </Typography>
  ) : null

  const renderClosed = () => {
    const { reason, revisit = [] } = data.assessment ?? {}
    if (reason === 'passed') {
      return (
        <>
          <PageHeading ref={headingRef}>You have passed this assessment</PageHeading>
          <Typography sx={{ mb: 2 }}>
            {data.feedbackSubmitted
              ? 'Your certificate is ready.'
              : 'There is one short feedback form to complete, then you will get your certificate.'}
          </Typography>
          <Button
            component={RouterLink}
            to={data.feedbackSubmitted ? `${coursePath}/certificate` : `${coursePath}/feedback`}
            variant="contained"
          >
            {data.feedbackSubmitted ? 'Go to your certificate' : 'Go to the feedback form'}
          </Button>
        </>
      )
    }
    if (reason === 'revisit') {
      const modules = revisit.map(
        (moduleId) => data.content?.modules?.find((module) => module.id === moduleId)?.title ?? moduleId,
      )
      return (
        <>
          <PageHeading ref={headingRef}>Look at these modules again first</PageHeading>
          <Typography sx={{ mb: 1 }}>
            Before your next attempt, open every screen of {modules.length === 1 ? 'this module' : 'these modules'}{' '}
            again:
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
            {modules.map((moduleTitle) => (
              <Typography component="li" key={moduleTitle}>
                {moduleTitle}
              </Typography>
            ))}
          </Box>
          <Typography sx={{ mb: 2 }}>The assessment opens again once you have done that.</Typography>
          <Button component={RouterLink} to={coursePath} variant="contained">
            Back to the course
          </Button>
        </>
      )
    }
    return (
      <>
        <PageHeading ref={headingRef}>The assessment is not open yet</PageHeading>
        <Typography sx={{ mb: 2 }}>
          You need to finish every screen of the course before you can take the assessment.
        </Typography>
        <Button component={RouterLink} to={coursePath} variant="contained">
          Back to the course
        </Button>
      </>
    )
  }

  const renderStart = () => {
    const questionCount = data.content?.pathway?.draw
    const resuming = !!data.openAttemptId
    return (
      <>
        <PageHeading ref={headingRef}>Assessment: {title}</PageHeading>
        <SectionHeading sx={{ mt: 0 }}>Before you start</SectionHeading>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, '& li': { mb: 1 } }}>
          {questionCount ? (
            <Typography component="li">
              There are {questionCount} questions.
            </Typography>
          ) : null}
          <Typography component="li">You need {passMark}% to pass.</Typography>
          {course?.retakePolicy ? <Typography component="li">{course.retakePolicy}</Typography> : null}
          <Typography component="li">
            The questions, and the order of the answers, are chosen at random each time.
          </Typography>
          <Typography component="li">There is no time limit.</Typography>
          <Typography component="li">A certificate is only issued after passing.</Typography>
        </Box>
        {resuming && (
          <Typography sx={{ mb: 2 }}>You have an attempt in progress. You will carry on with the same questions.</Typography>
        )}
        <Button variant="contained" onClick={begin} disabled={busy}>
          {resuming ? 'Continue assessment' : 'Start assessment'}
        </Button>
        <Typography sx={{ mt: 2 }}>
          <RouterLink to={coursePath}>Back to the course</RouterLink>
        </Typography>
        {bankLink}
      </>
    )
  }

  const renderQuestions = () => (
    <>
      <PageHeading ref={headingRef} sx={{ fontSize: '1.3rem !important' }}>
        {onReview ? 'Check and submit your answers' : `Assessment: ${title}`}
      </PageHeading>

      {onReview ? (
        <Box>
          {unanswered.length ? (
            <>
              <Typography sx={{ mb: 1 }}>
                You need to answer every question before you can submit. {unanswered.length === 1 ? 'This question still needs' : 'These questions still need'} an answer:
              </Typography>
              <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
                {unanswered.map(({ question, questionIndex }) => (
                  <li key={question.id}>
                    <Button variant="text" onClick={() => setIndex(questionIndex)} sx={{ px: 0.5 }}>
                      Question {questionIndex + 1}
                      {question.type === 'multi' ? ` (select ${neededCount(question)})` : ''}
                    </Button>
                  </li>
                ))}
              </Box>
            </>
          ) : (
            <Typography sx={{ mb: 2 }}>
              You have answered all {total} questions. You can go back and change any answer before you submit.
            </Typography>
          )}
        </Box>
      ) : (
        <QuestionCard
          key={questions[index].id}
          question={questions[index]}
          number={index + 1}
          total={total}
          selected={answers[questions[index].id] ?? []}
          onChange={(chosen) => setAnswers((previous) => ({ ...previous, [questions[index].id]: chosen }))}
        />
      )}

      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => setIndex(index - 1)} disabled={index === 0 || busy}>
          Back
        </Button>
        {onReview ? (
          <Button variant="contained" onClick={() => setConfirmOpen(true)} disabled={unanswered.length > 0 || busy}>
            Submit answers
          </Button>
        ) : (
          <Button variant="contained" onClick={() => setIndex(index + 1)}>
            {index === total - 1 ? 'Review answers' : 'Next'}
          </Button>
        )}
      </Stack>

      <QuestionNavigator
        questions={questions}
        answeredIds={answeredIds}
        currentIndex={onReview ? -1 : index}
        onSelect={setIndex}
      />
      {!onReview && (
        <Button variant="text" onClick={() => setIndex(total)} sx={{ mt: 1, px: 0.5 }}>
          Go to review and submit
        </Button>
      )}
      {bankLink}

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        aria-labelledby="submit-assessment-title"
        sx={contentTextSx}
      >
        <DialogTitle id="submit-assessment-title" sx={{ fontFamily: 'inherit', fontWeight: 700 }}>
          Submit your answers?
        </DialogTitle>
        <DialogContent>
          <Typography>You cannot change your answers after you submit.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} variant="outlined">
            Go back
          </Button>
          <Button onClick={submit} variant="contained">
            Submit answers
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )

  const renderResult = () => {
    const score = `${result.score} out of ${result.total} (${result.percentage}%)`
    if (result.passed) {
      return (
        <>
          <PageHeading ref={headingRef}>
            <CheckCircleIcon aria-hidden="true" color="success" sx={{ verticalAlign: 'text-bottom', mr: 1 }} />
            Congratulations, you passed
          </PageHeading>
          <Typography sx={{ mb: 1 }}>Your score: {score}.</Typography>
          <Typography sx={{ mb: 2 }}>One short feedback form, then your certificate.</Typography>
          <Button component={RouterLink} to={`${coursePath}/feedback`} variant="contained">
            Go to the feedback form
          </Button>
          {bankLink}
        </>
      )
    }
    return (
      <>
        <PageHeading ref={headingRef}>
          <ErrorOutlineIcon aria-hidden="true" sx={{ verticalAlign: 'text-bottom', mr: 1 }} />
          You did not pass this time
        </PageHeading>
        <Typography sx={{ mb: 1 }}>Your score: {score}.</Typography>
        <Typography sx={{ mb: 2 }}>The pass mark is {result.passMark}%.</Typography>
        {result.missedModules?.length > 0 && (
          <>
            <SectionHeading>These are the modules to look at again before your next attempt</SectionHeading>
            <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
              {result.missedModules.map((module) => (
                <Typography component="li" key={module.id}>
                  {module.title}
                </Typography>
              ))}
            </Box>
          </>
        )}
        {result.missedOutcomes?.some((outcome) => outcome.text) && (
          <>
            <SectionHeading>What to focus on</SectionHeading>
            <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, '& li': { mb: 0.5 } }}>
              {result.missedOutcomes
                .filter((outcome) => outcome.text)
                .map((outcome) => (
                  <Typography component="li" key={outcome.id}>
                    {outcome.text}
                  </Typography>
                ))}
            </Box>
          </>
        )}
        <Typography sx={{ mb: 2, fontWeight: 700 }}>
          A certificate is only issued after passing the assessment.
        </Typography>
        {course?.retakePolicy ? <Typography sx={{ mb: 2 }}>{course.retakePolicy}</Typography> : null}
        <Button component={RouterLink} to={coursePath} variant="contained">
          Back to the course
        </Button>
        {bankLink}
      </>
    )
  }

  return (
    <ContentFrame>
      {step === 'loading' && (
        <Box role="status">
          <Typography sx={{ mb: 1 }}>Loading the assessment…</Typography>
          <LinearProgress />
        </Box>
      )}
      {step === 'loadError' && (
        <>
          <PageHeading ref={headingRef}>Assessment</PageHeading>
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
          <RouterLink to="/mandatory-training">Back to my training</RouterLink>
        </>
      )}
      {/* The result is announced when it arrives. */}
      <Box aria-live="polite">
        {step === 'closed' && renderClosed()}
        {step === 'result' && renderResult()}
      </Box>
      {step === 'start' && renderStart()}
      {step === 'questions' && renderQuestions()}
      <Box aria-live="assertive" sx={{ mt: 2 }}>
        {actionError && (
          <Alert severity="error" icon={<ErrorOutlineIcon />}>
            {actionError}
          </Alert>
        )}
      </Box>
      {busy && <LinearProgress aria-label="Working" sx={{ mt: 2 }} />}
    </ContentFrame>
  )
}

export default Assessment
